import React from 'react'
import {
  ActivityFeedItem,
  ActivityFeedItemWithReplies,
  ActivityFeedWhammyDispatchAction,
} from '@classy/campaign-page-blocks'
import { useActivityFeedReducer } from './useFeedItemsReducer'
import {
  addFeedItem,
  getInitialFeedItems,
  getMoreFeedItems,
  getComments,
  addComment,
  deleteFeedItem,
  deleteComment,
  likeFeedItem,
  likeComment,
  unlikeLike,
} from 'services/activityFeed/activity-feed'
import { formatFeedItem } from './formatFeedItem'
import { FeedItemWithContext, FeedItemCommentWithContext } from './feed-item.model'
import { StudioDonationMatchingPlanDto } from 'models/studio'
import { logger } from 'utils/logger'

/**
 * Actions dispatched by the Activity Feed block. These actions serve as a contract between the
 * block and Whammy for how to update the feed items list.
 */
type WhammyAction = ActivityFeedWhammyDispatchAction | { type: 'GET_INITIAL_FEED_ITEMS' }

/**
 * APIv2 comment to UI reply mapper
 */
export const toReplyEntity = (dto: FeedItemCommentWithContext): ActivityFeedItem => {
  const { id, commenter_name, body, created_at, likes, commenter, commentable_id } = dto

  return {
    id: String(id),
    name: commenter_name,
    parentFeedItemId: String(commentable_id),
    agentId: String(commenter.id),
    created: created_at,
    avatar: commenter.thumbnail_small || '',
    content: body || '',
    type: 'reply',
    likes,
  }
}

/**
 * APIv2 feed item to UI feed item (autogenerated feed items and user comments) mapper
 */
export const toFeedItemEntity = (
  dto: FeedItemWithContext,
  donationMatchingPlans?: StudioDonationMatchingPlanDto[],
): ActivityFeedItemWithReplies => {
  const {
    id,
    agent_name,
    agent_id,
    created_at,
    comment,
    comments_count,
    latest_comments,
    likes,
    linkable,
    type,
  } = dto

  return {
    id: String(id),
    name: agent_name || 'Anonymous',
    agentId: String(agent_id),
    avatar: getAvatar(dto, donationMatchingPlans ?? []),
    created: created_at,
    content: type === 'comment' ? comment || '' : formatFeedItem(dto, donationMatchingPlans),
    likes,
    replies: latest_comments.map((comment) => toReplyEntity(comment)),
    repliesCount: comments_count,
    isAnonymous: linkable && 'is_anonymous' in linkable ? linkable?.is_anonymous : false,
    type: type === 'comment' ? 'comment' : 'other',
  }
}

/**
 * Fetches the avatar based on the feed item type.
 */
export const getAvatar = (
  dto: FeedItemWithContext,
  donationMatchingPlans: StudioDonationMatchingPlanDto[],
) => {
  const { agent, linkable } = dto
  // for feed items created by a member (by donating or commenting on the activity feed)
  if (agent && 'thumbnail_small' in agent) {
    return agent.thumbnail_small ?? ''
  }

  // for the creation of a sponsor matching plan
  if (linkable && dto.linkable_type === 'donation_matching_plan') {
    const donationMatchingPlan = donationMatchingPlans.find(
      (donationMatchingPlan) => donationMatchingPlan.id === linkable.id,
    )
    return donationMatchingPlan?.business_image_url ?? ''
  }

  // for sponsor matched donations
  if (linkable && 'donation_matching_plan_id' in linkable && linkable.donation_matching_plan_id) {
    const donationMatchingPlan = donationMatchingPlans.find(
      (donationMatchingPlan) => donationMatchingPlan.id === linkable.donation_matching_plan_id,
    )
    return donationMatchingPlan?.business_image_url ?? ''
  }

  return ''
}

/**
 * Handles persisting activity feed data changes (to APIv2) and updating the feed items list.
 *
 * It is modeled after a useReducer, utilizing the same pattern for communicating what
 * action to perform, and passing along a payload.
 *
 * See https://bitbucket.org/stayclassy/campaign-page-blocks/pull-requests/657 for a useful
 * overview diagram.
 *
 * ! It is important to note that this hook is the middle layer between APIv2 and UI, which results
 * ! in a confusing mix of naming. Keep the following in mind:
 * !
 * ! - Action names (and payload) are UI-specific.
 * ! - Every action follows a similar pattern:
 * !   1. API call(s).
 * !      - API (services/activityFeed/activity-feed.ts) function names are APIv2-specific.
 * !      - Returns data using APIv2-specific naming.
 * !   2. Translate APIv2 data to UI data.
 * !   3. Update the UI (useActivityFeedReducer.ts).
 * !      - useActivityFeedReducer action names (and payload) are UI-specific.
 *
 * ## feedItemsState
 *
 * The list of activity feed items and the total items in the whole feed.
 *
 * ! All updates to feed items should only be done through whammyDispatch().
 *
 * ## whammyDispatch()
 *
 * Callback for reporting/publishing activity feed data changes. Per the dispatched action,
 * an API call is made, and if successful, "feedItems" is updated (via useFeedItemsReducer).
 */
export const useWhammyDispatch = (
  campaignId: string,
  donationMatchingPlans?: StudioDonationMatchingPlanDto[],
) => {
  const [feedItemsState, dispatch] = useActivityFeedReducer()

  const whammyDispatch = React.useCallback(
    async (action: WhammyAction) => {
      switch (action.type) {
        /**
         * Fetches the initial list of feed items, a combination of auto-generated items by APIv2
         * and user comments.
         *
         * - APIv2 "feed items" get converted into UI "feed items" (includes "user comments").
         * - In the UI, feed items and user comments are the same.
         * - A "user comment" is submitted by a member.
         * - An "auto-generated item" represents a system action, like "XYZ made a donation".
         */
        case 'GET_INITIAL_FEED_ITEMS': {
          const { items, total } = await getInitialFeedItems(campaignId)

          // Migrate Dto to UI
          const feedItems = items.map((item) => toFeedItemEntity(item, donationMatchingPlans))

          dispatch({
            type: 'SET_INITIAL_FEED_ITEMS_STATE',
            state: {
              items: feedItems,
              currentPage: 1,
              total,
            },
          })
          break
        }
        /**
         * Fetches the next batch of feed items, a combination of auto-generated items by APIv2
         * and user comments, to render. Feed items are fetched newest to oldest.
         */
        case 'SHOW_MORE_FEED_ITEMS': {
          try {
            const rawFeedItems = await getMoreFeedItems(campaignId, action.currentPage + 1)

            // Migrate Dto to UI
            const feedItems = rawFeedItems.map((item) =>
              toFeedItemEntity(item, donationMatchingPlans),
            )

            dispatch({
              type: 'APPEND_FEED_ITEMS',
              page: action.currentPage + 1,
              items: feedItems,
            })
          } catch (e) {
            const error = new Error('Unable to SHOW_MORE_FEED_ITEMS', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw e
          }
          break
        }
        /**
         * Truncate the activity feed to its initial state of four feed items.
         * This action does not perform any fetching.
         */
        case 'RESET_FEED': {
          try {
            dispatch({ type: 'RESET_FEED' })
          } catch (e) {
            const error = new Error('Unable to RESET_FEED', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw e
          }
          break
        }
        /**
         * Persists a new user comment submitted by the logged-in member.
         *
         * - A user comment is persisted as a FeedItem in APIv2.
         */
        case 'ADD_USER_COMMENT': {
          try {
            const rawFeedItem = await addFeedItem(campaignId, action.body)

            // Migrate Dto to UI
            const feedItem = toFeedItemEntity(rawFeedItem, donationMatchingPlans)

            /**
             * Inject avatar manually because APIv2 does not return it as part of the FeedItem DTO.
             * Assumes the passed avatar belongs to the logged-in user (see campaign page blocks).
             */
            feedItem.avatar = action.memberAvatarUrl

            dispatch({ type: 'ADD_USER_COMMENT', feedItem })
          } catch (e) {
            const error = new Error('Unable to ADD_USER_COMMENT', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw error
          }
          break
        }
        /**
         * Persists a new user reply to a comment submitted by the logged-in member.
         *
         * - A user reply is persisted as a FeedItemComment in APIv2.
         */
        case 'ADD_REPLY': {
          try {
            const rawFeedItemReply = await addComment(action.feedItemId, action.body)

            // Migrate Dto to UI
            const feedItemReply = toReplyEntity(rawFeedItemReply)

            /**
             * Inject avatar manually because APIv2 does not return it as part of the FeedItem DTO.
             * Assumes the passed avatar belongs to the logged-in user (see campaign page blocks).
             */
            feedItemReply.avatar = action.memberAvatarUrl

            dispatch({ type: 'ADD_REPLY', feedItemId: action.feedItemId, feedItemReply })
          } catch (e) {
            const error = new Error('Unable to ADD_REPLY', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw error
          }
          break
        }
        /**
         * Fetches the replies for a single feed item.
         *
         * - APIv2 comment = UI reply.
         * - When the feed item was initially fetched, it included the latest two replies. This
         *   action replaces them (no specific reason, just for simplicity).
         */
        case 'GET_REPLIES': {
          try {
            const rawReplies = await getComments(action.feedItemId)

            // Migrate Dto to UI
            const replies = rawReplies.map((reply) => toReplyEntity(reply))

            dispatch({ type: 'SET_REPLIES', feedItemId: action.feedItemId, replies })
          } catch (e) {
            const error = new Error('Unable to GET_REPLIES', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw e
          }
          break
        }
        /**
         * Deleting a user comment feed item is a two-action process:
         * - START_DELETE_USER_COMMENT (this action)
         *   - Delete the feed item in APIv2.
         *   - Set transitionType on the feed item to trigger an animation.
         * - FINISH_DELETE_USER_COMMENT
         *   - Remove the user comment feed item from ActivityFeedItemsState.
         */
        case 'START_DELETE_USER_COMMENT': {
          try {
            await deleteFeedItem(action.feedItemId)
            dispatch({ type: 'START_DELETE_USER_COMMENT', feedItemId: action.feedItemId })
          } catch (e) {
            const error = new Error('Unable to START_DELETE_USER_COMMENT', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw e
          }
          break
        }
        /**
         * See START_DELETE_USER_COMMENT for context.
         */
        case 'FINISH_DELETE_USER_COMMENT': {
          dispatch({ type: 'FINISH_DELETE_USER_COMMENT', feedItemId: action.feedItemId })
          break
        }
        /**
         * Deleting a reply is a two-action process:
         *
         * Note, UI = reply, and APIv2 = comment.
         *
         * - START_DELETE_REPLY (this action)
         *   - Delete the comment in APIv2.
         *   - Set transitionType on the reply to trigger an animation.
         * - FINISH_DELETE_REPLY
         *   - Remove the reply from ActivityFeedItemsState.
         */
        case 'START_DELETE_REPLY': {
          try {
            const { parentFeedItemId, replyId } = action
            await deleteComment(replyId)
            dispatch({ type: 'START_DELETE_REPLY', parentFeedItemId, replyId })
          } catch (e) {
            const error = new Error('Unable to START_DELETE_REPLY', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw e
          }
          break
        }

        /**
         * See START_DELETE_REPLY for context.
         */
        case 'FINISH_DELETE_REPLY': {
          const { parentFeedItemId, replyId } = action
          dispatch({ type: 'FINISH_DELETE_REPLY', parentFeedItemId, replyId })
          break
        }

        case 'LIKE_FEED_ITEM': {
          try {
            const like = await likeFeedItem(action.feedItemId)
            dispatch({ type: 'LIKE_FEED_ITEM', feedItemId: action.feedItemId, likeId: like.id })
          } catch (e) {
            const error = new Error('Unable to LIKE_FEED_ITEM', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw e
          }
          break
        }

        case 'LIKE_REPLY': {
          try {
            const like = await likeComment(action.replyId)
            dispatch({
              type: 'LIKE_REPLY',
              parentFeedItemId: action.parentFeedItemId,
              replyId: action.replyId,
              likeId: like.id,
            })
          } catch (e) {
            const error = new Error('Unable to LIKE_REPLY', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw e
          }

          break
        }

        case 'UNLIKE_FEED_ITEM': {
          try {
            await unlikeLike(action.likeId)

            dispatch({
              type: 'UNLIKE_FEED_ITEM',
              feedItemId: action.feedItemId,
              likeId: action.likeId,
            })
          } catch (e) {
            const error = new Error('Unable to UNLIKE_FEED_ITEM', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw e
          }
          break
        }

        case 'UNLIKE_REPLY': {
          try {
            await unlikeLike(action.likeId)
            dispatch({
              type: 'UNLIKE_REPLY',
              parentFeedItemId: action.parentFeedItemId,
              replyId: action.replyId,
              likeId: action.likeId,
            })
          } catch (e) {
            const error = new Error('Unable to UNLIKE_REPLY', { cause: e })
            logger('error', error, { context: { campaignId, action } })
            throw e
          }

          break
        }

        default:
          /* @ts-expect-error -- TS complains about action.type because all actions are covered 💪 */
          throw Error(`Unknown action: ${action.type}`)
      }
    },
    [campaignId, dispatch, donationMatchingPlans],
  )

  return { feedItemsState, whammyDispatch }
}
